/**
 * Safari-safe clipboard helpers.
 *
 * `navigator.clipboard.writeText` / `readText` fail in Safari when the call
 * is not a direct user gesture, when clipboard permission was never granted,
 * or on older WebKit. Copy falls back to a temporary textarea, which Safari
 * still accepts inside a click. Paste cannot be forced; callers should focus
 * the field so the user can press Paste.
 */

/** Copy plain text. Resolves false when both the Clipboard API and the fallback fail. */
export async function copyText(text: string): Promise<boolean> {
  const value = text ?? "";
  if (!value) return false;

  if (canUseAsyncClipboard() && navigator.clipboard.writeText) {
    try {
      await navigator.clipboard.writeText(value);
      return true;
    } catch {
      // Safari throws NotAllowedError outside a fresh click, or when the
      // page is not a secure context. The textarea path still works on click.
    }
  }
  return copyWithExecCommand(value);
}

/**
 * Copy text that is not ready until a promise resolves (for example after an
 * HTTP call). Safari only keeps the user-gesture if `clipboard.write` is
 * started synchronously with a `ClipboardItem` whose blob is a Promise.
 * Call this directly from the click handler, before any await.
 */
export function copyTextWhenReady(
  textPromise: Promise<string>,
): Promise<boolean> {
  const pending = textPromise.then((text) => text ?? "");

  if (
    canUseAsyncClipboard() &&
    navigator.clipboard.write &&
    hasClipboardItem()
  ) {
    const blobPromise = pending.then((text) => {
      if (!text) throw new Error("Nothing to copy");
      return new Blob([text], { type: "text/plain" });
    });
    const item = new ClipboardItem({ "text/plain": blobPromise });
    return navigator.clipboard
      .write([item])
      .then(() => true)
      .catch(async () => {
        try {
          const text = await pending;
          return text ? copyWithExecCommand(text) : false;
        } catch {
          return false;
        }
      });
  }

  return pending
    .then((text) => (text ? copyText(text) : false))
    .catch(() => false);
}

/** Read clipboard text. Returns null when Safari blocks `readText`. */
export async function readClipboardText(): Promise<string | null> {
  if (!canUseAsyncClipboard() || !navigator.clipboard.readText) return null;
  try {
    const text = await navigator.clipboard.readText();
    return text ?? "";
  } catch {
    return null;
  }
}

function canUseAsyncClipboard(): boolean {
  return (
    typeof navigator !== "undefined" &&
    !!navigator.clipboard &&
    typeof window !== "undefined" &&
    window.isSecureContext
  );
}

function hasClipboardItem(): boolean {
  return typeof ClipboardItem !== "undefined";
}

function copyWithExecCommand(text: string): boolean {
  if (typeof document === "undefined") return false;

  const area = document.createElement("textarea");
  area.value = text;
  area.setAttribute("readonly", "");
  area.setAttribute("aria-hidden", "true");
  area.style.position = "fixed";
  area.style.top = "0";
  area.style.left = "0";
  area.style.width = "1px";
  area.style.height = "1px";
  area.style.padding = "0";
  area.style.border = "none";
  area.style.outline = "none";
  area.style.boxShadow = "none";
  area.style.background = "transparent";
  area.style.opacity = "0";
  area.style.pointerEvents = "none";

  const selection = document.getSelection();
  const saved =
    selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;

  document.body.appendChild(area);
  area.focus();
  area.select();
  area.setSelectionRange(0, text.length);

  let ok = false;
  try {
    ok = document.execCommand("copy");
  } catch {
    ok = false;
  }

  area.remove();
  if (saved && selection) {
    selection.removeAllRanges();
    selection.addRange(saved);
  }
  return ok;
}
